/**
 * ReportTypePreferencesFields — per-type preferences form for the
 * merged Configuration step.
 *
 * Rerun Reimagined RR4. Closes the deferred "config-form override
 * per type" gap noted in each wrapper panel's header comment.
 *
 * Dispatches by `reportType` via the existing PreferencesFormFor —
 * environmental users edit NEPA scope / species / wetlands; power-
 * availability users edit BTM strategy / substation tier; etc.
 *
 * Adds RR4-specific UI sugar on top of the raw form:
 *   - schema label + caption so the panel announces what's being edited
 *   - "Reset to v(N) values" link that reverts to the saved snapshot
 *   - a "changed since v(N)" indicator (small dot + count)
 *
 * Configuration Upgrade (CU pack) extends this with:
 *   - CU1: three-band field hierarchy (score-affecting / recommended /
 *     optional) rendered as a header strip above the form.
 *   - CU2: inline required-field highlighting via aria-invalid + a nav-
 *     style footer pointing at offending fields.
 *   - CU3: cross-field warnings surfaced inline below the form with
 *     optional one-click suggestedFix CTAs.
 *   - CU4: "Affects score" chip rendered on score-affecting bands.
 *   - CU5: cross-type spillover hint below the form when the site has
 *     other active report types using overlapping fields.
 *   - CU6: DAM-aware per-field suggestion stub (no-ops when DAM doesn't
 *     yet carry defaultPrefsByArchetype — to be populated later).
 *   - CU7: "what the AI will see" preview rendering the live output of
 *     the corresponding buildXxxContext function.
 *
 * The component never mutates the rerun pipeline — it's a controlled
 * form, the parent owns `value` and the merged-typedPreferences merge
 * still happens in handleRerun.
 */
import React, { useMemo } from 'react';
import { PreferencesFormFor, validatePreferencesFor } from '../preferences';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { TypedPreferencesByType } from '../../../types/siteRag';
import {
  PREFS_SCHEMA_LABEL,
  PREFS_SCHEMA_CAPTION,
  PREFS_BAND_MAP,
} from '../../../types/rerunPrefsByType';
import { FieldBand } from '../preferences/FieldBand';
import { labelFor } from '../../../lib/reportPreferences/labels';
import { PrefsValidationProvider } from '../preferences/shared';
import { crossFieldWarnings } from '../preferences/crossFieldWarnings';
import { CrossFieldWarningList } from '../preferences/CrossFieldWarning';
import { CrossTypeSpillover } from '../preferences/CrossTypeSpillover';
import { DamSuggestionPanel } from '../preferences/DamSuggestionPanel';
// AiContextPreview renders the production prompt builders, which pull the whole
// prompt corpus (services/reportPrompts, ~640 KB of text) into whatever chunk
// imports it. Load it on demand so the preferences form and the entry bundle
// stay free of it.
const AiContextPreview = React.lazy(() =>
  import('../preferences/AiContextPreview').then((m) => ({ default: m.AiContextPreview })),
);
import type { DatacrateModel } from '../../../types/datacrateModel';
import type { CompanyArchetype } from '../../../types/company';
import type { CompanyContextInput } from '../../../services/reportPrompts';
import type { SavedCustomBrief } from '../../../types/site';
import type { CustomBriefPreferences } from '../../../types/siteRag';
import { RotateCcw } from 'lucide-react';

export interface ReportTypePreferencesFieldsProps<T extends ReportTypeId> {
  reportType: T;
  /** Current edited prefs. The form mutates this via onChange. */
  value: NonNullable<TypedPreferencesByType[T]>;
  /** Persisted baseline — used for the changed-fields count + Reset action. */
  baseline: NonNullable<TypedPreferencesByType[T]>;
  onChange: (next: NonNullable<TypedPreferencesByType[T]>) => void;
  /**
   * Other report types active on the same site — used by CU5 to filter
   * the cross-type spillover hints to types the user actually consumes.
   * Optional; when omitted the spillover panel is hidden.
   */
  availableOtherTypes?: ReportTypeId[];
  /**
   * Site/company archetype — drives the CU6 DAM-default suggestions.
   * Optional; suggestions are stubbed when omitted.
   */
  archetype?: CompanyArchetype;
  /**
   * DAM for the current report type — read by CU6 to pull
   * defaultPrefsByArchetype. Optional; when absent (or the DAM
   * doesn't carry the map) the panel renders nothing.
   */
  dam?: DatacrateModel;
  /**
   * Reader-profile / company context — when provided, the CU7
   * preview pane prepends a READER PROFILE block to the USER CONTEXT
   * output. Optional; without it the preview shows just the
   * USER CONTEXT block.
   */
  companyContext?: CompanyContextInput;
  /** Custom Brief template wiring for rerun mode. */
  customBriefSite?: {
    id: string;
    savedCustomBriefs?: SavedCustomBrief[];
  };
  onSaveCustomBrief?: (preferences: CustomBriefPreferences, existingId?: string) => Promise<string>;
  onDeleteCustomBrief?: (briefId: string) => Promise<void>;
}

/** Count of top-level keys whose serialised value differs from baseline. */
export function countChangedFields(
  edited: Record<string, unknown>,
  baseline: Record<string, unknown>,
): number {
  const keys = new Set([...Object.keys(edited), ...Object.keys(baseline)]);
  let n = 0;
  for (const k of keys) {
    const a = edited[k];
    const b = baseline[k];
    const same = typeof a === 'object' || typeof b === 'object'
      ? JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
      : a === b;
    if (!same) n += 1;
  }
  return n;
}

export function ReportTypePreferencesFields<T extends ReportTypeId>({
  reportType,
  value,
  baseline,
  onChange,
  availableOtherTypes,
  archetype,
  dam,
  companyContext,
  customBriefSite,
  onSaveCustomBrief,
  onDeleteCustomBrief,
}: ReportTypePreferencesFieldsProps<T>) {
  const changedCount = countChangedFields(
    value as unknown as Record<string, unknown>,
    baseline as unknown as Record<string, unknown>,
  );
  const validation = validatePreferencesFor(reportType, value);
  const bandMap = PREFS_BAND_MAP[reportType];

  // CU3 — compute cross-field warnings live and route them two ways:
  //   - per-field inline (via PrefsValidationContext.warningsByField),
  //     where the relevant field's FieldRow renders the message
  //     beneath the input;
  //   - footer list — only for warnings whose `field` doesn't render
  //     in a FieldRow (e.g. KPI weights live in a Tabs panel; latency
  //     targets live in a nested grid). Footer rendering avoids
  //     dropping warnings the inline path can't reach.
  const warnings = useMemo(
    () => crossFieldWarnings(reportType, value as TypedPreferencesByType[ReportTypeId]),
    [reportType, value],
  );

  // CU2/CU4 — build the validation context that FieldRow reads to
  // render aria-invalid + the inline "Required" badge + the "Affects
  // score" chip. missingRequiredKeys is new on the ValidationResult
  // shape (Configuration Upgrade); validators that haven't migrated
  // yet return undefined and the badge falls back silently to the
  // footer banner.
  const warningsByField = useMemo(() => {
    const acc: Record<string, { severity: 'warn' | 'error'; message: string }> = {};
    for (const w of warnings) {
      if (!acc[w.field]) acc[w.field] = { severity: w.severity, message: w.message };
    }
    return acc;
  }, [warnings]);

  const prefsCtxValue = useMemo(
    () => ({
      missingRequiredKeys: validation.missingRequiredKeys ?? [],
      scoreAffectingKeys: bandMap?.scoreAffecting ?? [],
      warningsByField,
    }),
    [validation.missingRequiredKeys, bandMap, warningsByField],
  );

  // CU3 — apply a suggested fix by merging the patch into the current
  // value. Callers wire this to the one-click CTAs.
  const handleApplyFix = (fixField: string, suggested: unknown) => {
    onChange({
      ...(value as Record<string, unknown>),
      [fixField]: suggested,
    } as unknown as NonNullable<TypedPreferencesByType[T]>);
  };

  return (
    <div
      className="border border-border p-3 space-y-3 rounded-lg"
      data-testid="rerun-report-type-prefs"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
            {PREFS_SCHEMA_LABEL[reportType]}
          </div>
          <div className="type-meta text-text-muted mt-0.5">
            {PREFS_SCHEMA_CAPTION[reportType]}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {changedCount > 0 && (
            <span
              className="inline-flex items-center gap-1 type-meta uppercase tracking-widest text-warning"
              data-testid="rerun-report-type-prefs-changed-count"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-warning" aria-hidden="true" />
              {changedCount} changed
            </span>
          )}
          {changedCount > 0 && (
            <button
              type="button"
              onClick={() => onChange(baseline)}
              className="inline-flex items-center gap-1 type-meta uppercase tracking-widest text-text-secondary hover:text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
              data-testid="rerun-report-type-prefs-reset"
            >
              <RotateCcw className="w-2.5 h-2.5" aria-hidden="true" /> Reset to v(N)
            </button>
          )}
        </div>
      </div>

      {/* CU1 — three-band field hierarchy summary. The actual inputs
           are still rendered by PreferencesFormFor below; this strip
           tells the user which fields move the score, which are
           recommended, and which are optional frame-setters. The
           optional band collapses into a <details> on click so the
           happy path stays clean. */}
      {bandMap && (
        <div
          className="space-y-2 border border-border-subtle p-2 bg-background/40 rounded-lg"
          data-testid="rerun-prefs-band-summary"
        >
          {bandMap.scoreAffecting.length > 0 && (
            <FieldBand band="score-affecting">
              <BandFieldList reportType={reportType} fields={bandMap.scoreAffecting} />
            </FieldBand>
          )}
          {bandMap.recommended.length > 0 && (
            <FieldBand band="recommended">
              <BandFieldList reportType={reportType} fields={bandMap.recommended} />
            </FieldBand>
          )}
          {bandMap.optional.length > 0 && (
            <FieldBand band="optional">
              <BandFieldList reportType={reportType} fields={bandMap.optional} />
            </FieldBand>
          )}
        </div>
      )}

      <PrefsValidationProvider value={prefsCtxValue}>
        <PreferencesFormFor
          reportType={reportType}
          value={value as TypedPreferencesByType[T]}
          onChange={(next) => onChange(next as NonNullable<TypedPreferencesByType[T]>)}
          site={customBriefSite}
          onSaveCustomBrief={onSaveCustomBrief}
          onDeleteCustomBrief={onDeleteCustomBrief}
        />
      </PrefsValidationProvider>

      {/* CU3 — footer list of cross-field warnings whose `field`
           doesn't sit in a visible FieldRow. The inline path renders
           the message beside each field; this catches the leftovers
           (KPI weights, latency targets, etc.) AND provides the
           one-click suggestedFix CTA which the inline path doesn't
           render. */}
      {warnings.length > 0 && (
        <CrossFieldWarningList warnings={warnings} onApplyFix={handleApplyFix} />
      )}

      {/* CU6 — DAM-aware suggestion chips. Stub-tolerant: renders
           nothing when DAM lacks defaultPrefsByArchetype (true for
           most pre-CU6 builds). */}
      <DamSuggestionPanel
        reportType={reportType}
        value={value}
        archetype={archetype}
        dam={dam}
        onAccept={handleApplyFix}
      />

      {/* CU5 — cross-type spillover hints. Filters the static map by
           the other report types active on the site so we don't pitch
           env hints to a feasibility-only site. */}
      {availableOtherTypes && availableOtherTypes.length > 0 && (
        <CrossTypeSpillover
          currentType={reportType}
          availableOtherTypes={availableOtherTypes}
        />
      )}

      {/* CU7 — "what the AI will see" preview. Renders the production
           buildXxxContext output verbatim, updates live on every edit
           (deterministic computation, no debounce needed). Collapsed
           by default so the panel doesn't dominate the column. */}
      <React.Suspense fallback={null}>
        <AiContextPreview
          reportType={reportType}
          value={value as TypedPreferencesByType[ReportTypeId]}
          company={companyContext}
        />
      </React.Suspense>

      {/* CU2 — footer banner becomes a navigation aid pointing at the
           inline-highlighted offending fields. Accessibility surface
           uses aria-live=polite so screen readers announce the count
           when it changes. */}
      {!validation.valid && validation.missingRequired.length > 0 && (
        <div
          className="type-body text-warning border border-warning/30 bg-warning/5 px-2 py-1.5 rounded"
          data-testid="rerun-report-type-prefs-missing"
          role="status"
          aria-live="polite"
        >
          {validation.missingRequired.length} required field{validation.missingRequired.length === 1 ? '' : 's'} missing — see highlighted above ({validation.missingRequired.join(' · ')}).
        </div>
      )}
    </div>
  );
}

/** Renders the field-key list for a band as a compact comma-joined
 *  string of human-readable labels. The actual inputs sit in the main
 *  PreferencesFormFor below — this strip is purely a navigational
 *  hint. */
const BandFieldList: React.FC<{
  reportType: ReportTypeId;
  fields: string[];
}> = ({ reportType, fields }) => (
  <div className="type-body text-text-secondary leading-relaxed">
    {fields.map((f, i) => (
      <span key={f} data-testid={`band-field-${f}`}>
        {labelFor(reportType, f)}
        {i < fields.length - 1 ? ' · ' : ''}
      </span>
    ))}
  </div>
);
